<template>
    <div class="specs-component flex flex-col items-center">
        <h1 class="title step-title"> {{ $t('step-4.title') }} </h1>
        <form @submit.prevent="handleFormSubmit">
            <div class="specs-inputs-container flex flex-col items-center">
                <p class="warning self-start mb-10 select-none">
                    {{ $t('required-field') }}
                </p>
                <div class="spec flex items-center w-full px-1" v-for="spec in specsFiltered" :key="spec.text" :style="{ order: spec.orders[orderIndex] }" :class="specClasses(spec)">
                    <span class="text flex-grow select-none">{{ $t(spec.text) }}</span>
                    <input type="text"
                        class="ml-2 mr-2 sm:mr-5"
                        @keydown="checkIfNumeric($event)"
                        @input="set(spec.prop, $event)"
                        @blur="removeTrailingChars(spec.prop, $event)"
                        @keyup.enter="handleFormSubmit"
                        :value="values[spec.prop]"
                        :placeholder="'ej: ' + spec.placeholder"
                        inputmode="decimal"
                        :tabindex="spec.orders[orderIndex]" />
                    <span class="unit text-center select-none">{{ spec.unit }}</span>
                </div>
                <button
                    class="btn btn-primary flex items-center justify-center mt-10 mb-8"
                    :class="{ disabled: formIsInvalid }"
                    :style="{ order: specsFiltered.length + 1 }"
                >
                    <span v-if="loading" class="spinner"></span>
                    <span v-else>{{ $t('next') }}</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { ACTIONS, GETTERS, MUTATIONS } from "../../../store/wizard/types";
import next from '../../../mixins/next';

export default {
    mixins: [next],

    data() {
        return {
            specs: [
    { text: this.extraText('step-4.voltage', null, this.isRequired('voltage')), unit: 'V', prop: 'voltage', orders: [null, null, null, 1, 3, 1, 1, 5, 1], required: this.isRequired('voltage'), placeholder: '750' },
    { text: this.extraText('step-4.voltage', 'voltageRMS', this.isRequired('voltage')), unit: 'Vrms', prop: 'rmsVoltage', orders: [1, 3, 1, null, null, null, 2, 6, 2], required: this.isRequired('voltage'), placeholder: '270' },
    { text: this.extraText('step-4.power', 'powerAC', true), unit: 'KW', prop: 'acPower', orders: [2, 2, 3, null, null, null, 3, 3, 5], required: true, placeholder: '7.5' },
    { text: this.extraText('step-4.power', 'powerDC', true), unit: 'KW', prop: 'dcPower', orders: [null, null, null, 2, 2, 3, 4, 4, 6], required: true, placeholder: '3.75' },
    { text: this.extraText('step-4.current', 'currentAC', this.isRequired('current')), unit: 'A', prop: 'acCurrent', orders: [3, 1, 2, null, null, null, 5, 1, 3], required: this.isRequired('current'), placeholder: '10' },
    { text: this.extraText('step-4.current', 'currentDC', this.isRequired('current')), unit: 'A', prop: 'dcCurrent', orders: [null, null, null, 3, 1, 2, 6, 2, 4], required: this.isRequired('current'), placeholder: '10' },
    { text: this.extraText('step-4.frequency'), unit: 'Hz', prop: 'frequency', orders: [4, 4, 4, null, null, null, 7, 7, 7], required: false, placeholder: '100' },
],

            values: {
                voltage: this.$store.state.consult.voltage || '',
                rmsVoltage: this.$store.state.consult.rmsVoltage || '',
                acPower: this.$store.state.consult.acPower || '',
                dcPower: this.$store.state.consult.dcPower || '',
                acCurrent: this.$store.state.consult.acCurrent || '',
                dcCurrent: this.$store.state.consult.dcCurrent || '',
                frequency: this.$store.state.consult.frequency || ''
            },
            orderIndex: 0,
            autoCalculate: null,
            loading: false,
            toastError: null,
        }
    },

    computed: {
        ...mapGetters({
            currentType: GETTERS.GET_CURRENT_TYPE,
            setpoint: GETTERS.GET_SETPOINT,
            consult: GETTERS.GET_CONSULT
        }),
        specsFiltered() {
            return this.specs.filter(spec => spec.orders[this.orderIndex] !== null);
        },
        formIsInvalid() {
            return this.specsFiltered.find(spec => {
                const value = this.values[spec.prop];
                return (spec.required && ! value) || value >= 100000;
            });
        },
    },

    methods: {
        ...mapMutations({
            setConsult: MUTATIONS.SET_CONSULT,
        }),
        getTranslatedText(textKey) {
            return this.$t(textKey);
        },
        specClasses({ orders }) {
            const order = orders[this.orderIndex];
            return {
                hidden: order === null,
                'border-top': order !== 1,
                'pt-0': order === 1,
                'pb-0': this.specsFiltered.length === order
            };
        },
        checkIfNumeric(e) {
            const k = e.keyCode;
            // 110, 190 => .
            if (! ([8, 9, 35, 36, 37, 39, 46, 110, 190].includes(k) || (k >= 48 && k <= 57) || (k >= 96 && k <= 105))) {
                e.preventDefault();
            }
        },
        removeTrailingChars(prop, e) {
            let value = e.target.value;
            while (value.includes('.') && ['.', '0'].includes(value.slice(-1))) {
                value = value.slice(0, -1);
            }
            e.target.value = value;
            this.values[prop] = value;
        },
        set(prop, e) {
            const value = e.target.value;
            if (value === '') {
                this.values[prop] = null;
                return;
            }
            if (! /^((\d{1,5})|(\d{1,5}\.\d{0,2}))$/g.test(value)) {
                e.target.value = this.values[prop] || '';
            } else {
                this.values[prop] = value;
            }
        },
        setAutoCalculated(prop) {
            // TODO: pending confirmation (keep power/current auto-fill?)
            return;

            const { acPower, dcPower, acCurrent, dcCurrent, voltage: standardVoltage, rmsVoltage, frequency } = this.values;
            const power = acPower || dcPower;
            const current = acCurrent || dcCurrent;
            const voltage = standardVoltage || rmsVoltage;

            if (this.currentType.name === 'AC/DC') {
                if (prop === 'acPower') {
                    this.values.dcPower = this.values.acPower;
                } else if (prop === 'acCurrent') {
                    this.values.dcCurrent = this.values.acCurrent;
                }
            }

            if (Array.isArray(this.autoCalculate)) {
                if (this.autoCalculate[0] === 'acPower') {
                    // Calculate AC and DC power
                    if (! ['acPower', 'dcPower'].includes(prop) && acCurrent && standardVoltage) {
                        const power = Number((acCurrent * standardVoltage).toFixed(2));
                        this.values.acPower = power;
                        this.values.dcPower = power;
                    }
                } else {
                    // Calculate AC and DC intensities
                    if (! ['acCurrent', 'dcCurrent'].includes(prop) && acPower && standardVoltage) {
                        const current = Number((acPower / standardVoltage).toFixed(2));
                        this.values.acCurrent = current;
                        this.values.dcCurrent = current;
                    }
                }
            } else if (this.autoCalculate.includes('Power') && ! prop.includes('Power') && current && voltage) {
                this.values[this.autoCalculate] = Number((current * voltage).toFixed(2));
            } else if (['voltage', 'rmsVoltage'].includes(this.autoCalculate) && ! ['voltage', 'rmsVoltage'].includes(prop) && power && current) {
                this.values[this.autoCalculate] = Number((power / current).toFixed(2));
            } else if (this.autoCalculate.includes('Current') && ! prop.includes('Current') && power && voltage) {
                this.values[this.autoCalculate] = Number((power / voltage).toFixed(2));
            }
        },
        extraText(spec, specCurrentType, isAlwaysRequired) {
            const currentTypeText = specCurrentType ? ` (${this.$t(specCurrentType)})` : '';
            const asterisk = (isAlwaysRequired || this.isRequired(spec)) ? '*' : '';
            const translatedSpec = this.$t(spec);

            // Concatenar el texto adicional solo si hay contenido en currentTypeText o asterisk
            const additionalText = (currentTypeText || asterisk) ? `${currentTypeText}${asterisk}` : '';

            return `${translatedSpec}${additionalText}`;
        },

        isRequired(spec) {
            switch (this.$store.state.consult.setpoint) {
                case 1:
                    return spec === 'voltage';
                case 2:
                    return spec === 'current';
                case 3:
                    return true;
            }
        },
        handleFormSubmit() {
            if (! this.formIsInvalid) {
                this.submit();
            }
        },
        submit() {
            this.loading = true;
            this.next('resume').catch(e => {
                this.loading = false;
            });
        }
    },

    watch: {
        values: {
            deep: true,
            handler(values) {
                values = { ...values };
                for (let [key, val] of Object.entries(values)) {
                    values[key] = Number(val);
                }
                this.setConsult({ ...this.consult, ...values });
            }
        }
    },

    created() {
        if (this.currentType.name === 'AC') {
            this.setConsult({ ...this.consult, dcPower: null });

            if (this.setpoint.id === 1) {
                this.autoCalculate = 'acCurrent';
            } else if (this.setpoint.id === 2) {
                this.orderIndex = 1;
                this.autoCalculate = 'rmsVoltage';
            } else if (this.setpoint.id === 3) {
                this.orderIndex = 2;
                this.autoCalculate = 'acPower';
            }
        } else if (this.currentType.name === 'DC') {
            this.setConsult({ ...this.consult, frequency: null });

            if (this.setpoint.id === 1) {
                this.orderIndex = 3;
                this.autoCalculate = 'dcCurrent';
            } else if (this.setpoint.id === 2) {
                this.orderIndex = 4;
                this.autoCalculate = 'voltage';
            } else if (this.setpoint.id === 3) {
                this.orderIndex = 5;
                this.autoCalculate = 'dcPower';
            }
        } else {
            if (this.setpoint.id === 1) {
                this.orderIndex = 6;
                this.autoCalculate = ['acCurrent', 'dcCurrent'];
            } else if (this.setpoint.id === 2) {
                this.orderIndex = 7;
                this.autoCalculate = 'voltage';
            } else if (this.setpoint.id === 3) {
                this.orderIndex = 8;
                this.autoCalculate = ['acPower', 'dcPower'];
            }
        }
    },

    async beforeRouteLeave (to, from, next) {
        if (to.name !== 'resume') {
            return next();
        }

        try {
            if (this.toastError) {
                this.toastError.dismiss();
            }
            await window.store.dispatch(ACTIONS.SAVE_CONSULT);
        } catch (e) {
            this.toastError = this.$toast.error(this.$t('errorMessages.something-wrong'));
            return next(false);
        }
        next();
    },
};
</script>
